import * as React from 'react';
import { forwardRef, SyntheticEvent, useEffect, useImperativeHandle, useState } from 'react';
import { Alert, CircularProgress, Grid, IconButton, Stack, Typography } from '@mui/material';
import { CurrencyText } from '../../components/input-formatters/CurrencyText';
import {
  GetRecurrencyDocument,
  IntegrationType,
  Recurrency,
  RecurrencyProcessorData,
  RecurrencyState,
  useGetRecurrencyLazyQuery,
  useGetRecurrencyProcessorDataLazyQuery,
  useSaveRecurrencyMutation
} from '../../graphql/types';
import { getFormattedDateUTC } from '../../utils/getFormattedDate';
import { openSnackbar } from '../../store/reducers/snackbar';
import { dispatch } from '../../store';
import { BugTwoTone, CaretRightOutlined, InfoCircleTwoTone, ReloadOutlined } from '@ant-design/icons';
import CustomTooltip from '../../components/CustomTooltip';
import { useTheme } from '@mui/material/styles';
import Loader from '../../components/Loader';
import { LoadingButton } from '@mui/lab';
import CopyTextIconButton from '../../components/CopyTextIconButton';

export interface Props {
  recurrency: Recurrency;
}

interface ForwardRef {
  reloadFromProcessor: () => void;
}

const RecurrencyAchDetails = forwardRef<ForwardRef, Props>(({ recurrency }: Props, ref) => {
  const theme = useTheme();
  const [elapsed, setElapsed] = useState(0);
  const [getRecurrencyProcessorData, { loading: loadingProcessorData }] = useGetRecurrencyProcessorDataLazyQuery({
    fetchPolicy: 'network-only'
  });
  const [getRecurrency] = useGetRecurrencyLazyQuery({
    fetchPolicy: 'network-only'
  });
  const [saveRecurrency, { loading: savingRecurrency }] = useSaveRecurrencyMutation();
  const [processorData, setData] = useState<RecurrencyProcessorData | null>(null);
  const [recurrencyData, setrRcurrencyData] = useState<Recurrency>(recurrency);
  const [processorError, setProcessorError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (dataLoaded) {
      return;
    }
    setProcessorError(null);
    if (!recurrencyData) {
      return;
    }
    let interval: NodeJS.Timeout;
    let elapsedInterval: NodeJS.Timeout;
    if (recurrencyData.state === RecurrencyState.New) {
      interval = setInterval(getRecurrencyData, 1000);
      elapsedInterval = setInterval(() => {
        setElapsed((t) => t + 0.1);
      }, 100);
    } else {
      setElapsed(0);
      loadRecurrencyProcessorData();
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
      if (elapsedInterval) {
        clearInterval(elapsedInterval);
      }
    };
  }, [recurrencyData]);

  const getRecurrencyData = async () => {
    if (!recurrencyData.id) {
      console.warn(`Recurrence ID is not set`);
      return;
    }
    const res = await getRecurrency({
      variables: { id: recurrencyData.id }
    });
    setrRcurrencyData(res.data?.recurrency as Recurrency);
  };

  const handleUpdate = (field: keyof Recurrency, value: any) => {
    return () => {
      saveRecurrency({
        variables: {
          id: recurrencyData.id,
          mcaId: '',
          integrationId: recurrencyData.integration.id,
          dto: Object.assign(
            {
              state: recurrencyData.state,
              amount: recurrencyData.amount,
              dailyPayment: recurrencyData.dailyPayment,
              description: recurrencyData.description,
              term: recurrencyData.term,
              startDate: recurrencyData.startDate,
              referenceRecurrence: recurrencyData.referenceRecurrence,
              routingNumber: recurrencyData.routingNumber,
              accountNumber: recurrencyData.accountNumber,
              processorReference: recurrencyData.processorReference
            },
            { [field]: value }
          )
        },
        refetchQueries: [{ query: GetRecurrencyDocument, variables: { id: recurrency.id } }]
      }).then((res) => {
        setDataLoaded(false);
        setrRcurrencyData((prevState) => {
          return {
            ...prevState,
            ...(res.data?.saveRecurrency as Recurrency)
          };
        });
      });
    };
  };

  useImperativeHandle(ref, () => ({
    reloadFromProcessor: () => loadRecurrencyProcessorData()
  }));

  const loadRecurrencyProcessorData = () => {
    if (!recurrencyData.integration.autostart) {
      setDataLoaded(true);
      return;
    }
    getRecurrencyProcessorData({
      variables: {
        recurrencyId: recurrencyData.id
      }
    }).then(({ data: processorData, error }) => {
      if (error) {
        setDataLoaded(true);
        setProcessorError(error.message || 'Unknown error');
        return;
      }
      if (!processorData?.getRecurrencyProcessorData) {
        setDataLoaded(true);
        return dispatch(
          openSnackbar({
            open: true,
            message: `Cannot resolve unique schedule for "${
              recurrencyData.referenceRecurrence || recurrencyData.referenceMca
            }". Check corresponding processor.`,
            variant: 'alert',
            alert: {
              color: 'warning'
            },
            close: true
          })
        );
      }
      setData(processorData.getRecurrencyProcessorData as RecurrencyProcessorData);
      setDataLoaded(true);
    });
  };

  const accountNumberMismatch = (processorAccountNumber?: string | null, crmAccountNumber?: string | null): boolean => {
    if (!processorAccountNumber || !crmAccountNumber) return false;
    if (processorAccountNumber.includes('*')) {
      const unmaskedDigits = processorAccountNumber.replace(/\*/g, '');
      return !crmAccountNumber.endsWith(unmaskedDigits);
    }
    return processorAccountNumber !== crmAccountNumber;
  };

  const AchStatusDialogContentText: React.FC<{ recurrency: Recurrency | null }> = () => {
    if (!recurrencyData) {
      return <></>;
    }

    if (!recurrencyData.integration.autostart) {
      return (
        <Alert color="warning" variant="outlined" icon={<InfoCircleTwoTone twoToneColor={theme.palette.info.main} />}>
          <Stack direction="column" pt={1} pb={1} spacing={1.5} justifyContent="center" alignItems="center">
            <Typography>
              Integration with this platform is currently inactive, change status in the platform integration settings and try again later.
            </Typography>
          </Stack>
        </Alert>
      );
    }

    if (recurrencyData.state === RecurrencyState.New) {
      return (
        <Alert color="info" variant="outlined" icon={<InfoCircleTwoTone twoToneColor={theme.palette.info.main} />}>
          <Stack direction="column" pt={1} pb={1} spacing={1.5} justifyContent="center" alignItems="center">
            <Typography>Recurrence data is being sent to ACH processor. {`${elapsed === 0 ? '-.-' : elapsed.toFixed(1)} s`}</Typography>
          </Stack>
        </Alert>
      );
    }

    if (recurrencyData.state === RecurrencyState.Error) {
      return (
        <Alert color="error" variant="outlined" icon={<BugTwoTone twoToneColor={theme.palette.error.main} />}>
          <Stack direction="column" pt={1} pb={1} spacing={1.5} justifyContent="center" alignItems="center">
            <Stack direction="column">
              <Typography color={'error'}>Recurrency data is not stored in ACH processor due to error:</Typography>
              <Typography color={'error'}>({recurrencyData.stateNotes || 'Unknown error'}).</Typography>
            </Stack>
            {!processorData && (
              <LoadingButton
                disabled={savingRecurrency}
                variant="outlined"
                color={'warning'}
                sx={{ height: 40, width: 200 }}
                startIcon={<ReloadOutlined />}
                onClick={(e: SyntheticEvent) => {
                  e.stopPropagation();
                  handleUpdate('state', RecurrencyState.New)();
                }}
              >
                Recreate in Platform
              </LoadingButton>
            )}
          </Stack>
        </Alert>
      );
    }

    if (!dataLoaded) {
      return <></>;
    }

    if (processorError) {
      return (
        <Alert color="error" variant="outlined" icon={<BugTwoTone twoToneColor={theme.palette.error.main} />}>
          <Stack direction="column" pt={1} pb={1} spacing={1.5} justifyContent="center" alignItems="center">
            <Typography>Cannot connect to the platform, verify platform integration settings and try again later.</Typography>
            <Typography color={'error'}>({processorError}).</Typography>
          </Stack>
        </Alert>
      );
    }

    return (
      <Alert color="info" variant="outlined" icon={<InfoCircleTwoTone twoToneColor={theme.palette.info.main} />}>
        <Typography variant="h5" pt={2} pb={2} fontSize={14}>
          Review recurrency data stored in ACH processor. Optionally you can update CRM data.
        </Typography>
      </Alert>
    );
  };
  return (
    <>
      {loadingProcessorData && <Loader />}
      <Grid container spacing={1} pb={4}>
        <Grid item>
          <AchStatusDialogContentText recurrency={recurrencyData} />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={3}></Grid>
        <Grid item xs={4}>
          <Stack direction={'row'} spacing={1.25}>
            <Typography fontWeight={'bold'}>ACH Processor</Typography> {!dataLoaded && <CircularProgress size={20} />}
          </Stack>
        </Grid>
        <Grid item xs={1}></Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>CRM</Typography>
        </Grid>
        {(processorData?.processorReference || recurrencyData.processorReference) && (
          <>
            <Grid item xs={3}>
              <Typography>Reference:</Typography>
            </Grid>
            <Grid item xs={4}>
              <Stack direction="row" alignItems="center">
                <Typography fontSize={14}>{processorData?.processorReference}</Typography>
                {processorData?.processorReference && (
                  <CopyTextIconButton textToCopy={processorData?.processorReference || ''} dispatchText={'Recurrency Reference'} />
                )}
              </Stack>
            </Grid>
            <Grid item xs={1}>
              {processorData && processorData.processorReference !== recurrencyData.processorReference && (
                <CustomTooltip title="Overwrite with ACH processor value">
                  <IconButton sx={{ color: theme.palette.skyblue?.main }} size="small" disableRipple onClick={handleUpdate('processorReference', processorData.processorReference)}>
                    <CaretRightOutlined />
                  </IconButton>
                </CustomTooltip>
              )}
            </Grid>
            <Grid item xs={4}>
              <Stack direction="row" alignItems="center">
                <Typography fontSize={14}>{recurrencyData?.processorReference}</Typography>
                {recurrencyData?.processorReference && (
                  <CopyTextIconButton textToCopy={recurrencyData?.processorReference || ''} dispatchText={'Recurrency Reference'} />
                )}
              </Stack>
            </Grid>
          </>
        )}
        <Grid item xs={3}>
          <Typography>Account number:</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>
            {recurrencyData?.integration?.type === IntegrationType.DevNull ? '' : processorData?.accountNumber}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          {recurrencyData?.integration?.type !== IntegrationType.GoAch && recurrencyData?.integration?.type !== IntegrationType.DevNull && processorData && accountNumberMismatch(processorData.accountNumber, recurrencyData.accountNumber) && (
            <CustomTooltip title="Overwrite with ACH processor value">
              <IconButton sx={{ color: theme.palette.skyblue?.main }} size="small" disableRipple onClick={handleUpdate('accountNumber', processorData.accountNumber)}>
                <CaretRightOutlined />
              </IconButton>
            </CustomTooltip>
          )}
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>{recurrencyData.accountNumber}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography>Routing number:</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>
            {recurrencyData?.integration?.type === IntegrationType.DevNull ? '' : processorData?.routingNumber}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          {recurrencyData?.integration?.type !== IntegrationType.GoAch && recurrencyData?.integration?.type !== IntegrationType.DevNull && processorData && processorData.routingNumber !== recurrencyData.routingNumber && (
            <CustomTooltip title="Overwrite with ACH processor value">
              <IconButton sx={{ color: theme.palette.skyblue?.main }} size="small" disableRipple onClick={handleUpdate('routingNumber', processorData.routingNumber)}>
                <CaretRightOutlined />
              </IconButton>
            </CustomTooltip>
          )}
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>{recurrencyData.routingNumber}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography>Start date:</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>
            {recurrencyData?.integration?.type === IntegrationType.DevNull ? '' : getFormattedDateUTC(processorData?.startDate)}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          {recurrencyData?.integration?.type !== IntegrationType.DevNull && processorData && getFormattedDateUTC(processorData.startDate) !== getFormattedDateUTC(recurrencyData.startDate) && (
            <CustomTooltip title="Overwrite with ACH processor value">
              <IconButton sx={{ color: theme.palette.skyblue?.main }} size="small" disableRipple onClick={handleUpdate('startDate', processorData.startDate)}>
                <CaretRightOutlined />
              </IconButton>
            </CustomTooltip>
          )}
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>{getFormattedDateUTC(recurrencyData.startDate)}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography>Payment:</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>
            {processorData ? (
              recurrencyData?.integration?.type === IntegrationType.DevNull ? '' : <CurrencyText value={processorData?.amount || -1} />
            ) : recurrencyData?.integration?.type === IntegrationType.DevNull ? '' : 'UNKNOWN'}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          {recurrencyData?.integration?.type !== IntegrationType.DevNull && processorData && processorData.amount && recurrencyData.dailyPayment !== processorData.amount && (
            <CustomTooltip title="Save payment amount">
              <IconButton sx={{ color: theme.palette.skyblue?.main }} size="small" disableRipple onClick={handleUpdate('dailyPayment', processorData?.amount)}>
                <CaretRightOutlined />
              </IconButton>
            </CustomTooltip>
          )}
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}><CurrencyText value={recurrencyData.dailyPayment} /></Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography>Active:</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>
            {recurrencyData?.integration?.type === IntegrationType.DevNull ? '' : processorData?.isActive ? 'Yes' : 'No'}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          {processorData && processorData.isActive && recurrencyData.state !== RecurrencyState.Active && (
            <CustomTooltip title="Set recurrency state to Active">
              <IconButton sx={{ color: theme.palette.skyblue?.main }} size="small" disableRipple onClick={handleUpdate('state', RecurrencyState.Active)}>
                <CaretRightOutlined />
              </IconButton>
            </CustomTooltip>
          )}
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>{recurrencyData.state === RecurrencyState.Active ? 'Yes' : 'No'}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography>Paused:</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>
            {recurrencyData?.integration?.type === IntegrationType.DevNull ? '' : processorData?.isPaused ? 'Yes' : 'No'}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          {processorData && processorData.isPaused && recurrencyData.state !== RecurrencyState.Paused && (
            <CustomTooltip title="Set recurrency state to Paused">
              <IconButton sx={{ color: theme.palette.skyblue?.main }} size="small" disableRipple onClick={handleUpdate('state', RecurrencyState.Paused)}>
                <CaretRightOutlined />
              </IconButton>
            </CustomTooltip>
          )}
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>{recurrencyData.state === RecurrencyState.Paused ? 'Yes' : 'No'}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography>Stopped:</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>
            {recurrencyData?.integration?.type === IntegrationType.DevNull ? '' : processorData?.isStopped ? 'Yes' : 'No'}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          {processorData && processorData.isStopped && recurrencyData.state !== RecurrencyState.Stopped && (
            <CustomTooltip title="Set recurrency state to Stopped">
              <IconButton sx={{ color: theme.palette.skyblue?.main }} size="small" disableRipple onClick={handleUpdate('state', RecurrencyState.Stopped)}>
                <CaretRightOutlined />
              </IconButton>
            </CustomTooltip>
          )}
        </Grid>
        <Grid item xs={4}>
          <Typography fontWeight={'bold'}>{recurrencyData.state === RecurrencyState.Stopped ? 'Yes' : 'No'}</Typography>
        </Grid>
      </Grid>
    </>
  );
});

export default RecurrencyAchDetails;
